import { toast } from 'sonner'

import { useEffect, useRef, useState, createContext, use, useCallback, useTransition } from 'react'
import { X } from '@phosphor-icons/react'
import { createStore, useStore } from 'zustand'
import { Input, Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import { Badge } from '../ui/badge'
import { SupportedLanguage } from '@/i18n/constants'

type GenericBadgeItem = {
  id: string
  financial_model_id: string
  type: `custom` | `default` | 'required'
  name?: string | null
  description?: Record<SupportedLanguage, string | null>
  can_delete?: boolean
  can_be_deselected?: boolean
  deselect_forbidden_reason?: string | null
}

interface EditableBadgeProps {
  items: GenericBadgeItem[]
  selected_items?: GenericBadgeItem[]
  error?: string | null
}

interface EditableBadgeState extends EditableBadgeProps {
  setItems: (items: GenericBadgeItem[]) => void
}

type EditableBadgeStore = ReturnType<typeof createEditableBadgeStore>

const createEditableBadgeStore = (initialProps: EditableBadgeProps) => {
  const DEFAULT_PROPS: EditableBadgeProps = {
    items: [],
    selected_items: [],
    error: null
  }
  return createStore<EditableBadgeState>()(set => ({
    ...DEFAULT_PROPS,
    ...initialProps,
    setItems: items => set({ items })
  }))
}

const useBadgeError = () => {
  const store = use(EditableBadgeContext)
  if (!store) throw new Error(`EditableBadgeContext not found`)

  const id = useRef<string | number | null>(null)

  useEffect(() => {
    const unsubscribe = store.subscribe(state => {
      if (state.error) {
        const toastId = toast.error(state.error)
        id.current = toastId
      }
      if (!state.error && id.current) {
        toast.dismiss(id.current)
        id.current = null
      }
    })
    return () => unsubscribe()
  }, [store])
}

const EditableBadgeContext = createContext<EditableBadgeStore | null>(null)

type Timer = ReturnType<typeof setTimeout>

const EditableBadge = ({
  item,
  isSelected,
  toggle,
  update
}: {
  item: GenericBadgeItem
  isSelected: boolean
  toggle: (id: string) => void
  update: (
    items: {
      id: string
      name: string
      is_selected: boolean
    }[]
  ) => void
}) => {
  const store = use(EditableBadgeContext)
  if (!store) throw new Error(`EditableBadge must be used within a EditableBadgeProvider`)

  const items = useStore(store, state => state.items)
  const selected_items = useStore(store, state => state.selected_items)
  const isItemSelected = useStore(store, state => state.selected_items?.find(selectedItem => selectedItem.id === item.id))

  //   const setItems = useStore(store, (state) => state.setItems)

  const [editingBadge, setEditingBadge] = useState(false)
  const editingBadgeRef = useRef<HTMLInputElement>(null)
  const [newDepartmentName, setNewDepartmentName] = useState(item.name)
  const [, startTransition] = useTransition()
  const timer = useRef<Timer | null>(null)

  const handleDoubleClick = () => {
    if (item.type !== `custom`) return
    if (timer.current) clearTimeout(timer.current)
    startTransition(() => {
      setEditingBadge(true)
    })
  }

  const handleClick = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    } else {
      if (item.type === `required`) return
      let delay = 500
      if (item.type !== `custom`) delay = 0
      timer.current = setTimeout(() => {
        if (!editingBadge) {
          toggle(item.id)
          if (isItemSelected) {
            store.setState(state => ({
              ...state,
              items: item.type === `custom` ? state.items.filter(dep => dep.id !== item.id) : state.items,
              selected_items: state.selected_items?.filter(selectedItem => selectedItem.id !== item.id)
            }))
          } else {
            store.setState(state => ({
              ...state,
              items: item.type === `custom` ? state.items.filter(dep => dep.id !== item.id) : state.items,
              selected_items: [...(state.selected_items ?? []), item]
            }))
          }

          timer.current = null
        }
      }, delay)
    }
  }

  useEffect(() => {
    if (editingBadge) editingBadgeRef.current?.focus()
  }, [editingBadge])

  const handleBlur = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    if (newDepartmentName === item.name) return setEditingBadge(false)
    if (!newDepartmentName) return toggle(item.id)
    if (items.find(dep => dep.name?.toLowerCase().trim() === newDepartmentName.toLowerCase().trim())) {
      return store.setState({ error: `Department already exists` })
    }
    update([
      {
        id: item.id,
        name: newDepartmentName,
        is_selected: isSelected
      }
    ])
    const newItems = items.map(dep => (dep.id === item.id ? { ...dep, name: newDepartmentName } : dep))

    if (isItemSelected) {
      const newSelectedItems = selected_items?.map(selectedItem =>
        selectedItem.id === item.id ? { ...selectedItem, name: newDepartmentName } : selectedItem
      )
      store.setState({ items: newItems, selected_items: newSelectedItems })
    } else {
      store.setState({ items: newItems, selected_items })
    }
    startTransition(() => {
      setEditingBadge(false)
    })
  }

  const getBadgeVariant = useCallback(() => {
    switch (item.type) {
      case `required`:
        return `disabled`
      case `custom`:
      case `default`: {
        return isSelected ? `info` : `outline`
      }
      default:
        return `outline`
    }
  }, [item.type, isSelected])

  if (!editingBadge)
    return (
      <Button
        size='md'
        asChild
        variant='outline'
        className={cn(`cursor-pointer`, {
          'cursor-text': editingBadge,
          'cursor-not-allowed text-gray-500 opacity-50': item.type === `required` || item.can_be_deselected === false,
          'pointer-events-none': item.can_be_deselected === false,
          'hover:border-gray-500 hover:bg-none hover:text-gray-500': item.type === `required`
        })}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <Badge size='lg' variant={getBadgeVariant()}>
          {newDepartmentName ?? item.name}
          {isSelected && item.type !== `required` ? <X className='ml-2 text-blue-400' size={16} /> : null}
        </Badge>
      </Button>
    )

  const onFocus = () => {
    store.setState({ error: null })
  }
  return (
    <Badge
      size='lg'
      variant='outline'
      className={cn(`cursor-text p-0`, {
        'border-red-400': !!store.getState().error
      })}
    >
      <Input
        ref={editingBadgeRef}
        value={newDepartmentName ?? ``}
        className={cn(`h-10 w-fit min-w-0 rounded-full border-none`, {
          'text-red-400': !!store.getState().error
        })}
        placeholder={`Edit ${item.name}`}
        onBlur={handleBlur}
        onFocus={onFocus}
        onKeyDown={e => {
          if (e.key === `Enter`) e.currentTarget.blur()
        }}
        onChange={e => {
          setNewDepartmentName(e.target.value)
        }}
      />
    </Badge>
  )
}

interface CreateCategoryPayload {
  name: string
  [key: string]: any
}

interface BadgeWithInputProps<T extends CreateCategoryPayload> {
  onCreateCategory: (data: T) => Promise<GenericBadgeItem>
  createPayload: (name: string) => T
  placeholder?: string
}

const BadgeWithInput = <T extends CreateCategoryPayload>({
  onCreateCategory,
  createPayload,
  placeholder = `Add New Department`
}: BadgeWithInputProps<T>) => {
  const store = use(EditableBadgeContext)
  if (!store) throw new Error(`EditableBadgeContext not found`)
  const items = useStore(store, state => state.items)
  const [newCategoryName, setNewCategoryName] = useState(``)

  const addDepartment = useCallback(async () => {
    if (!newCategoryName) return
    if (items?.find(cat => cat.name?.toLowerCase().trim() === newCategoryName.toLowerCase().trim())) {
      store.setState({ error: `Category already exists` })
      return
    }
    const newCategory = await onCreateCategory(createPayload(newCategoryName))
    setNewCategoryName(``)
    store.setState(state => ({
      items: [...state.items, newCategory],
      selected_items: [...(state.selected_items ?? []), newCategory]
    }))
  }, [newCategoryName, items, onCreateCategory, createPayload, store])

  const onFocus = () => {
    store.setState({ error: null })
  }

  useBadgeError()

  return (
    <Badge
      size='lg'
      variant='outline'
      className={cn(`cursor-text p-0`, {
        'border-red-400': !!store.getState().error
      })}
    >
      <Input
        value={newCategoryName ?? ``}
        className={cn(`h-10 w-fit min-w-0 rounded-full border-none`, {
          'text-red-400': !!store.getState().error
        })}
        placeholder={placeholder}
        onBlur={addDepartment}
        onFocus={onFocus}
        onKeyDown={e => {
          if (e.key === `Enter`) e.currentTarget.blur()
        }}
        onChange={e => {
          setNewCategoryName(e.target.value)
        }}
      />
    </Badge>
  )
}

export { EditableBadge, EditableBadgeContext, createEditableBadgeStore, BadgeWithInput }
